<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue';
import { Skeletor } from 'vue-skeletor';
import 'vue-skeletor/dist/vue-skeletor.css';

export interface Props {
  fetching?: boolean;
  height?: string;
  width?: string;
  text?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  fetching: false,
  height: undefined,
  width: undefined,
  text: false,
});

const content = ref();
const style = ref();
const isFetching = ref(false);
const lines = ref(0);

const element = computed(() => {
  return content.value?.firstElementChild;
});

onMounted(() => {
  style.value = getComputedStyle(element.value);

  isFetching.value = props.fetching;

  if (props.text) {
    const divHeight = parseInt(element.value.offsetHeight, 10);
    const lineHeight = parseInt(style.value.lineHeight, 10);
    lines.value = divHeight && lineHeight ? divHeight / lineHeight : 0;
  }
})


const styling = computed(() => {
  return {
    height: style.value?.height,
    width: props.width ?? style.value?.width,
    borderRadius: style.value?.borderRadius,
    aspectRatio: style.value?.aspectRatio,
    margin: style.value?.margin,
    marginTop: style.value?.marginTop,
    marginBottom: style.value?.marginBottom,
    marginRight: style.value?.marginRight,
    marginLeft: style.value?.marginLeft,
    padding: style.value?.padding,
    fontSize: style.value?.fontSize,
  }
})

watch(
  () => props.fetching,
  (value) => {
    if(!value) {
      isFetching.value = value;
    }
  }
)
</script>

<template>
  <div v-if="props.text && isFetching" class="flex flex-col justify-around" :style="styling">
    <Skeletor
      v-for="line in lines"
      :key="line"
      :style="`height:${styling.fontSize};`"
      :width="props.width ?? styling.width"
    />
  </div>
  <Skeletor
    v-else-if="isFetching"
    as="div"
    :style="styling"
    :height="props.height ?? styling.height"
    :width="props.width ?? styling.width"
  />
  <!-- v-show used to make sure the slot is rendered and its styling can be used for the Skeletor -->
  <div v-show="!isFetching" ref="content" v-bind="$attrs">
    <slot />
  </div>
</template>
